/** `x-widget: "tag-mapping"` — edits an array of `tag_mapping` records.
 *
 * The grid cell shows an "N mappings" summary and an "Edit tag mapping…" button
 * that opens a `<Dialog maxWidth="md">` with one card per record plus add/remove.
 *
 * Record fields (only `tag_category` uses the tag-category autocomplete):
 *   path (required), tag_category, tag_context, name_attribute,
 *   attributes[{name,path}], all_properties_as_attributes,
 *   map ({raw:display} | [allowed…]), map_policy, map_case_insensitive,
 *   value_type.
 *
 * Only keys with a meaningful value are written to each output record (no empty
 * strings, empty arrays, or nulls). `map` is edited as a JSON textarea parsed on
 * blur — the last valid parse is kept and an inline error is shown on bad JSON.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';

import { TagCategoryAutocomplete } from './TagCategoryAutocomplete';

// ---------------------------------------------------------------------------
// Value model
// ---------------------------------------------------------------------------

type MapPolicy = 'reject' | 'allow';
type MapValue = Record<string, string> | string[];

interface AttributeSpec {
  name: string;
  path: string;
}

/** Draft (editable) shape of one record — `map` is kept as raw text so partial
 *  edits don't blow away the value; `mapError` surfaces the last parse error. */
interface DraftRecord {
  path: string;
  tag_category: string | null;
  tag_context: string;
  name_attribute: string;
  attributes: AttributeSpec[];
  all_properties_as_attributes: boolean;
  mapText: string;
  mapValue: MapValue | null;
  mapError: string | null;
  map_policy: MapPolicy | '';
  map_case_insensitive: boolean;
  value_type: string;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function parseMap(v: unknown): MapValue | null {
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'string' ? x : String(x)));
  if (isPlainObject(v)) {
    const out: Record<string, string> = {};
    for (const [k, val] of Object.entries(v)) out[k] = typeof val === 'string' ? val : String(val);
    return out;
  }
  return null;
}

function parseAttributes(v: unknown): AttributeSpec[] {
  if (!Array.isArray(v)) return [];
  const out: AttributeSpec[] = [];
  for (const item of v) {
    if (isPlainObject(item)) out.push({ name: str(item.name), path: str(item.path) });
  }
  return out;
}

function toDraft(rec: Record<string, unknown>): DraftRecord {
  const mapValue = parseMap(rec.map);
  const policy = rec.map_policy;
  return {
    path: str(rec.path),
    tag_category:
      typeof rec.tag_category === 'string' && rec.tag_category ? rec.tag_category : null,
    tag_context: str(rec.tag_context),
    name_attribute: str(rec.name_attribute),
    attributes: parseAttributes(rec.attributes),
    all_properties_as_attributes: Boolean(rec.all_properties_as_attributes),
    mapText: mapValue != null ? JSON.stringify(mapValue, null, 2) : '',
    mapValue,
    mapError: null,
    map_policy: policy === 'reject' || policy === 'allow' ? policy : '',
    map_case_insensitive: Boolean(rec.map_case_insensitive),
    value_type: str(rec.value_type),
  };
}

function blankDraft(): DraftRecord {
  return {
    path: '',
    tag_category: null,
    tag_context: '',
    name_attribute: '',
    attributes: [],
    all_properties_as_attributes: false,
    mapText: '',
    mapValue: null,
    mapError: null,
    map_policy: '',
    map_case_insensitive: false,
    value_type: '',
  };
}

function toArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isPlainObject);
}

/** Serialize a draft to the stored record, omitting empty/meaningless keys. */
function serialize(d: DraftRecord): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (d.path.trim()) out.path = d.path.trim();
  if (d.tag_category && d.tag_category.trim()) out.tag_category = d.tag_category.trim();
  if (d.tag_context.trim()) out.tag_context = d.tag_context.trim();
  if (d.name_attribute.trim()) out.name_attribute = d.name_attribute.trim();

  const attrs = d.attributes
    .map((a) => ({ name: a.name.trim(), path: a.path.trim() }))
    .filter((a) => a.name || a.path);
  if (attrs.length > 0) out.attributes = attrs;

  if (d.all_properties_as_attributes) out.all_properties_as_attributes = true;

  if (d.mapValue != null) {
    const nonEmpty = Array.isArray(d.mapValue)
      ? d.mapValue.length > 0
      : Object.keys(d.mapValue).length > 0;
    if (nonEmpty) out.map = d.mapValue;
  }
  if (d.map_policy) out.map_policy = d.map_policy;
  if (d.map_case_insensitive) out.map_case_insensitive = true;
  if (d.value_type.trim()) out.value_type = d.value_type.trim();
  return out;
}

// ---------------------------------------------------------------------------
// Attributes sub-list
// ---------------------------------------------------------------------------

interface AttributesEditorProps {
  attributes: AttributeSpec[];
  readOnly: boolean;
  onChange: (next: AttributeSpec[]) => void;
}

function AttributesEditor({ attributes, readOnly, onChange }: AttributesEditorProps) {
  function patch(index: number, p: Partial<AttributeSpec>) {
    onChange(attributes.map((a, i) => (i === index ? { ...a, ...p } : a)));
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        Extra attributes (name → JSONPath)
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {attributes.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            None
          </Typography>
        )}
        {attributes.map((attr, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Name"
              value={attr.name}
              disabled={readOnly}
              onChange={(e) => patch(index, { name: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="Path"
              value={attr.path}
              disabled={readOnly}
              onChange={(e) => patch(index, { path: e.target.value })}
              sx={{ flex: 1, '& input': { fontFamily: 'monospace' } }}
            />
            {!readOnly && (
              <IconButton
                size="small"
                onClick={() => onChange(attributes.filter((_, i) => i !== index))}
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
      </Stack>
      {!readOnly && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange([...attributes, { name: '', path: '' }])}
          sx={{ mt: 0.5, textTransform: 'none' }}
        >
          Add attribute
        </Button>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// One record card
// ---------------------------------------------------------------------------

interface RecordCardProps {
  toId: number;
  index: number;
  draft: DraftRecord;
  readOnly: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onChange: (patch: Partial<DraftRecord>) => void;
  onRemove: () => void;
}

function RecordCard({
  toId,
  index,
  draft,
  readOnly,
  expanded,
  onToggleExpand,
  onChange,
  onRemove,
}: RecordCardProps) {
  const noOutputKey = !draft.tag_category?.trim() && !draft.tag_context.trim();
  const summary = `${draft.path || '(no path)'} → ${
    draft.tag_category || draft.tag_context || '(no category)'
  }`;

  function commitMap() {
    const text = draft.mapText.trim();
    if (!text) {
      onChange({ mapValue: null, mapError: null });
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed) && !isPlainObject(parsed)) {
        onChange({ mapError: 'Map must be a JSON object or array.' });
        return;
      }
      onChange({ mapValue: parseMap(parsed), mapError: null });
    } catch (e) {
      // Keep the last valid mapValue; just surface the parse error.
      onChange({ mapError: e instanceof Error ? e.message : 'Invalid JSON' });
    }
  }

  return (
    <Box sx={{ p: expanded ? 2 : 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box
        onClick={onToggleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: expanded ? 1 : 0,
          cursor: 'pointer',
        }}
      >
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
          Mapping {index + 1}
        </Typography>
        {expanded ? (
          <Box sx={{ flex: 1 }} />
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ flex: 1, minWidth: 0, ml: 1, fontFamily: 'monospace' }}
          >
            {summary}
          </Typography>
        )}
        {!readOnly && (
          <Tooltip title="Remove mapping" placement="top" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {expanded && (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField
              label="Path"
              required
              size="small"
              fullWidth
              placeholder="JSONPath, or booking_note:…"
              value={draft.path}
              disabled={readOnly}
              error={!draft.path.trim()}
              helperText={!draft.path.trim() ? 'Path is required.' : ' '}
              onChange={(e) => onChange({ path: e.target.value })}
              sx={{ '& input': { fontFamily: 'monospace' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TagCategoryAutocomplete
              toId={toId}
              value={draft.tag_category}
              onChange={(code) => onChange({ tag_category: code })}
              readOnly={readOnly}
              label="Tag category"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Tag context"
              size="small"
              fullWidth
              placeholder="Output key name"
              value={draft.tag_context}
              disabled={readOnly}
              onChange={(e) => onChange({ tag_context: e.target.value })}
            />
          </Grid>

          {noOutputKey && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0 }}>
                Set at least one of <strong>tag category</strong> or <strong>tag context</strong>.
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              label="Name attribute"
              size="small"
              fullWidth
              placeholder="JSONPath"
              value={draft.name_attribute}
              disabled={readOnly}
              onChange={(e) => onChange({ name_attribute: e.target.value })}
              sx={{ '& input': { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Value type"
              size="small"
              fullWidth
              value={draft.value_type}
              disabled={readOnly}
              onChange={(e) => onChange({ value_type: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <AttributesEditor
              attributes={draft.attributes}
              readOnly={readOnly}
              onChange={(attributes) => onChange({ attributes })}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={draft.all_properties_as_attributes}
                  disabled={readOnly}
                  onChange={(e) => onChange({ all_properties_as_attributes: e.target.checked })}
                />
              }
              label="All properties as attributes"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider textAlign="left" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Value map (optional)
              </Typography>
            </Divider>
            <TextField
              label="Map (JSON object {raw: display} or array [allowed…])"
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={10}
              value={draft.mapText}
              disabled={readOnly}
              error={Boolean(draft.mapError)}
              helperText={draft.mapError ?? ' '}
              onChange={(e) => onChange({ mapText: e.target.value })}
              onBlur={commitMap}
              spellCheck={false}
              slotProps={{
                htmlInput: { style: { fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre' } },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Map policy"
              size="small"
              fullWidth
              value={draft.map_policy}
              disabled={readOnly}
              onChange={(e) => onChange({ map_policy: e.target.value as MapPolicy | '' })}
            >
              <MenuItem value="">
                <em>Default</em>
              </MenuItem>
              <MenuItem value="reject">reject</MenuItem>
              <MenuItem value="allow">allow</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={draft.map_case_insensitive}
                  disabled={readOnly}
                  onChange={(e) => onChange({ map_case_insensitive: e.target.checked })}
                />
              }
              label="Case-insensitive map"
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

interface DialogProps {
  open: boolean;
  toId: number;
  value: unknown;
  readOnly: boolean;
  onClose: () => void;
  onDone: (next: Record<string, unknown>[]) => void;
}

function TagMappingDialog({ open, toId, value, readOnly, onClose, onDone }: DialogProps) {
  const [drafts, setDrafts] = useState<DraftRecord[]>(() => toArray(value).map(toDraft));
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open) {
      setDrafts(toArray(value).map(toDraft));
      setExpanded(new Set()); // collapsed by default
    }
  }, [open, value]);

  function patch(index: number, p: Partial<DraftRecord>) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...p } : d)));
  }

  function toggleExpand(index: number) {
    setExpanded((prev) => {
      const s = new Set(prev);
      if (s.has(index)) s.delete(index);
      else s.add(index);
      return s;
    });
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Tag mapping</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {drafts.length === 0 && (
            <Typography variant="body2" color="text.disabled">
              No mappings yet.
            </Typography>
          )}
          {drafts.map((draft, index) => (
            <RecordCard
              key={index}
              toId={toId}
              index={index}
              draft={draft}
              readOnly={readOnly}
              expanded={expanded.has(index)}
              onToggleExpand={() => toggleExpand(index)}
              onChange={(p) => patch(index, p)}
              onRemove={() => {
                setDrafts((prev) => prev.filter((_, i) => i !== index));
                setExpanded(new Set()); // indices shift on remove — collapse all
              }}
            />
          ))}
        </Stack>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              const newIndex = drafts.length;
              setDrafts((prev) => [...prev, blankDraft()]);
              setExpanded((e) => new Set(e).add(newIndex)); // new record starts open
            }}
            sx={{ mt: 2, textTransform: 'none' }}
          >
            Add mapping
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onDone(drafts.map(serialize))}
          disabled={readOnly}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cell (rendered by the dispatcher)
// ---------------------------------------------------------------------------

interface CellProps {
  toId: number;
  value: unknown;
  onChange: (next: Record<string, unknown>[]) => void;
  readOnly?: boolean;
}

export function TagMappingCell({ toId, value, onChange, readOnly = false }: CellProps) {
  const [open, setOpen] = useState(false);
  const count = toArray(value).length;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
        {count} mapping{count !== 1 ? 's' : ''}
      </Typography>
      <Button
        size="small"
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', flexShrink: 0 }}
      >
        {readOnly ? 'View tag mapping…' : 'Edit tag mapping…'}
      </Button>
      <TagMappingDialog
        open={open}
        toId={toId}
        value={value}
        readOnly={readOnly}
        onClose={() => setOpen(false)}
        onDone={(next) => {
          onChange(next);
          setOpen(false);
        }}
      />
    </Box>
  );
}
